import { RefObject, useLayoutEffect, useState } from 'react';

const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

type UseConstrainedScrollAreaParams = {
  enabled: boolean;
  contentKey?: string | number;
  headerRef: RefObject<HTMLElement | null>;
  scrollRef: RefObject<HTMLElement | null>;
  maxModalHeight?: number;
  paddingYDesktop?: number;
  paddingYTablet?: number;
  overflowThreshold?: number;
};

type ConstrainedScrollArea = {
  maxHeight?: number;
  canScroll: boolean;
  isReady: boolean;
};

export const useConstrainedScrollArea = ({
  enabled,
  contentKey,
  headerRef,
  scrollRef,
  maxModalHeight = 698,
  paddingYDesktop = 80,
  paddingYTablet = 40,
  overflowThreshold = 16,
}: UseConstrainedScrollAreaParams): ConstrainedScrollArea => {
  const [maxHeight, setMaxHeight] = useState<number>();
  const [canScroll, setCanScroll] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      setMaxHeight(undefined);
      setCanScroll(false);
      setIsReady(false);
      return;
    }

    const measure = () => {
      const header = headerRef.current;
      const body = scrollRef.current;
      if (!header || !body) return;

      body.scrollTop = 0;

      if (window.innerWidth <= MOBILE_MAX) {
        body.style.maxHeight = '';
        setMaxHeight(undefined);
        setCanScroll(body.scrollHeight > body.clientHeight + overflowThreshold);
        setIsReady(true);
        return;
      }

      const modalBudget = Math.min(maxModalHeight, Math.round(window.innerHeight * 0.9));
      const overlayPadding = window.innerWidth <= TABLET_MAX ? paddingYTablet : paddingYDesktop;
      const availableForBody = Math.max(modalBudget - overlayPadding - header.offsetHeight, 0);

      body.style.maxHeight = 'none';
      const contentHeight = body.scrollHeight;
      body.style.maxHeight = '';

      const needsScroll = contentHeight - availableForBody > overflowThreshold;
      const nextMaxHeight = needsScroll ? availableForBody + 2 : undefined;

      if (needsScroll) body.style.maxHeight = `${nextMaxHeight}px`;
      else body.style.maxHeight = '';

      setMaxHeight(nextMaxHeight);
      setCanScroll(needsScroll);
      setIsReady(true);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [
    enabled,
    contentKey,
    headerRef,
    scrollRef,
    maxModalHeight,
    paddingYDesktop,
    paddingYTablet,
    overflowThreshold,
  ]);

  return { maxHeight, canScroll, isReady };
};
